import { RefObject, useRef } from 'react';

import { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { SaveSearchButton } from 'modules/search/components/SaveSearch/SaveSearchButton';
import { searchFacetsJob } from 'modules/search/constants/searchFacetsJob';
import { SearchContextHeaderData } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderData';
import { useSaveSearch } from 'modules/search/hooks/useSaveSearch';
import { useMainSelector } from 'store/hooks/useMainSelector';

import { ViewJobPageSidebarPagination } from './Pagination/ViewJobPageSidebarPagination';
import { ViewJobPageSidebarSuggestions } from './Suggestions/ViewJobPageSidebarSuggestions';
import { ViewJobPageSidebarSuggestionsExperiment } from './Suggestions/ViewJobPageSidebarSuggestionsExperiment';
import {
  ViewJobPageSidebarResultsHeader,
  ViewJobPageSidebarResultsHeaderTitle,
  ViewJobPageSidebarResultsHolder,
} from './ViewJobPageSidebarResults.styled';

type Props = {
  job: ApiJob;
  similarJobsExperimentEnabled: boolean;
  searchContext: SearchContextHeaderData | null | undefined;
  scrollingParentRef: RefObject<HTMLElement>;
};

export function ViewJobPageSidebarResults({
  job,
  similarJobsExperimentEnabled,
  searchContext: unstableSearchContext,
  scrollingParentRef,
}: Props) {
  /**
   * We only show the job search context on the sidebar if the user is coming
   * from a "job" search. If it's an "all" search, we don't show it because there
   * might be other types of results that aren't jobs.
   */
  const isJobSearch = useMainSelector(
    (state) => state.mainSearch.filters.type === 'JOB',
  );

  const results = useMainSelector(
    (state) => state.mainSearch.resultsByPage[state.mainSearch.pageIndex],
  );

  // The search context gets unset briefly when selecting a listing from a different page
  const searchContext = useRef(unstableSearchContext).current;

  const { saveSearch, saveableSearch } = useSaveSearch({
    searchFacets: searchFacetsJob,
  });

  const SuggestionsComponent = similarJobsExperimentEnabled
    ? ViewJobPageSidebarSuggestionsExperiment
    : ViewJobPageSidebarSuggestions;

  return (
    <ViewJobPageSidebarResultsHolder>
      {searchContext && isJobSearch && (
        <ViewJobPageSidebarResultsHeader>
          <ViewJobPageSidebarResultsHeaderTitle data-qa-id="search-view-title">
            {getText('Search View')}
          </ViewJobPageSidebarResultsHeaderTitle>

          {saveableSearch && (
            <SaveSearchButton
              saveSearch={saveSearch}
              searchFacets={searchFacetsJob}
            />
          )}
        </ViewJobPageSidebarResultsHeader>
      )}

      {searchContext && isJobSearch ? (
        <ViewJobPageSidebarPagination
          jobId={job.id}
          results={results}
          searchContext={searchContext}
          scrollingParentRef={scrollingParentRef}
        />
      ) : (
        <SuggestionsComponent job={job} />
      )}
    </ViewJobPageSidebarResultsHolder>
  );
}
